import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box,
    Text,
    Title,
    Paper,
    Alert,
    SimpleGrid,
    Badge,
    Divider,
    Accordion,
    Group,
    Anchor,
    Stack,
} from '@mantine/core';
import {
    IconSettings,
    IconAlertTriangle,
    IconShieldLock,
    IconCircleCheck,
    IconX,
    IconChevronDown,
    IconLink,
} from '@tabler/icons-react';
import { ServiceProviderConfig } from '../../types/ServiceProviderConfig';
import { ServiceProviderConfigService } from '../../services/ServiceProviderConfigService';
import { debugLog } from '../../utils/debugLog';
import { LoadingState } from '../common/LoadingState';

interface ServiceProviderConfigManagementProps {
    authResolved?: boolean;
}

export const ServiceProviderConfigManagement: React.FC<ServiceProviderConfigManagementProps> = ({
    authResolved = false
}) => {
    const [config, setConfig] = useState<ServiceProviderConfig | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const serviceProviderService = useMemo(() => new ServiceProviderConfigService(), []);

    const isLoadingRef = useRef(false);
    const hasLoadedInitialData = useRef(false);
    const previousAuthResolvedRef = useRef<boolean | null>(null);
    const fetchConfigRef = useRef<(() => Promise<void>) | null>(null);

    const fetchConfig = useCallback(async () => {
        if (isLoadingRef.current) {
            debugLog('⏭️ ServiceProviderConfigManagement: Load already in progress, skipping duplicate request');
            return;
        }

        isLoadingRef.current = true;
        setLoading(true);
        setError(null);
        try {
            debugLog('🔧 ServiceProviderConfigManagement: Fetching service provider configuration...');
            const data = await serviceProviderService.getServiceProviderConfig();
            setConfig(data);
        } catch (err) {
            console.error('❌ ServiceProviderConfigManagement: Error fetching service provider configuration:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch service provider configuration');
        } finally {
            isLoadingRef.current = false;
            setLoading(false);
        }
    }, [serviceProviderService]);

    useEffect(() => {
        fetchConfigRef.current = fetchConfig;
    }, [fetchConfig]);

    useEffect(() => {
        if (previousAuthResolvedRef.current === null) {
            previousAuthResolvedRef.current = authResolved;
            if (authResolved && !hasLoadedInitialData.current && !isLoadingRef.current) {
                debugLog('🔄 ServiceProviderConfigManagement: Initial load triggered (auth already resolved)');
                hasLoadedInitialData.current = true;
                if (fetchConfigRef.current) {
                    fetchConfigRef.current();
                }
            }
            return;
        }

        if (previousAuthResolvedRef.current === authResolved) {
            return;
        }
        previousAuthResolvedRef.current = authResolved;

        if (!authResolved) {
            debugLog('⏳ ServiceProviderConfigManagement: Waiting for authentication to be resolved...');
            return;
        }

        if (hasLoadedInitialData.current || isLoadingRef.current) {
            debugLog('⏭️ ServiceProviderConfigManagement: Already loaded or loading, skipping initial load');
            return;
        }

        debugLog('🔄 ServiceProviderConfigManagement: Initial load triggered by auth resolution');
        hasLoadedInitialData.current = true;
        if (fetchConfigRef.current) {
            fetchConfigRef.current();
        }
    }, [authResolved]);

    const renderFeatureStatus = (supported: boolean) => (
        <Badge
            leftSection={supported ? <IconCircleCheck size={14} /> : <IconX size={14} />}
            color={supported ? 'green' : 'gray'}
            variant="light"
            size="lg"
        >
            {supported ? 'Supported' : 'Not Supported'}
        </Badge>
    );

    const renderAuthenticationScheme = (scheme: any, index: number) => (
        <Paper key={index} withBorder p="md" mb="md">
            <Group gap="xs" align="center" mb="xs">
                <IconShieldLock size={20} color="var(--mantine-color-brand-6)" />
                <Title order={5}>{scheme.name}</Title>
            </Group>
            <Text size="sm" c="dimmed" mb="md">
                {scheme.description}
            </Text>
            <SimpleGrid cols={2}>
                <Text size="sm">
                    <strong>Type:</strong> {scheme.type}
                </Text>
                <Group gap={4} align="center">
                    <IconLink size={14} />
                    <Anchor href={scheme.specUri} target="_blank" size="sm">
                        Specification
                    </Anchor>
                </Group>
                <div />
                <Group gap={4} align="center">
                    <IconLink size={14} />
                    <Anchor href={scheme.documentationUri} target="_blank" size="sm">
                        Documentation
                    </Anchor>
                </Group>
            </SimpleGrid>
        </Paper>
    );

    if (loading) {
        return (
            <LoadingState variant="centered" minHeight={400} />
        );
    }

    return (
        <Box w="100%">
            {/* Warning Banner */}
            <Alert
                color="yellow"
                icon={<IconAlertTriangle size={20} />}
                mb="md"
            >
                <Text fw={700} size="sm" mb={4}>
                    This endpoint is temporarily unavailable
                </Text>
                <Text size="sm">
                    The Service Provider Config endpoint has been temporarily removed from the API.
                    This page will be restored once the endpoint is available again.
                </Text>
            </Alert>

            {/* Header */}
            <Paper withBorder p="lg" mb="lg" radius="md">
                <Group gap="md" align="center">
                    <IconSettings size={32} color="var(--mantine-color-brand-6)" />
                    <Title order={4}>
                        Service Provider Configuration
                    </Title>
                </Group>
            </Paper>

            {error ? (
                <Alert color="red" mt="md">
                    {error}
                </Alert>
            ) : !config ? (
                <Alert color="blue" mt="md">
                    No service provider configuration available.
                </Alert>
            ) : (
                <>
                    {/* Configuration Overview */}
                    <Paper withBorder p="lg" mb="lg">
                        <Title order={5} mb="lg">
                            Configuration Overview
                        </Title>

                        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                            <Paper withBorder p="md">
                                <Title order={5} mb="sm">
                                    Core Features
                                </Title>
                                <Stack gap="xs">
                                    <Group justify="space-between" align="center">
                                        <Text size="sm">PATCH Operations</Text>
                                        {renderFeatureStatus(config.patch.supported)}
                                    </Group>
                                    <Group justify="space-between" align="center">
                                        <Text size="sm">Bulk Operations</Text>
                                        {renderFeatureStatus(config.bulk.supported)}
                                    </Group>
                                    <Group justify="space-between" align="center">
                                        <Text size="sm">Filtering</Text>
                                        {renderFeatureStatus(config.filter.supported)}
                                    </Group>
                                    <Group justify="space-between" align="center">
                                        <Text size="sm">Sorting</Text>
                                        {renderFeatureStatus(config.sort.supported)}
                                    </Group>
                                    <Group justify="space-between" align="center">
                                        <Text size="sm">Change Password</Text>
                                        {renderFeatureStatus(config.changePassword.supported)}
                                    </Group>
                                    <Group justify="space-between" align="center">
                                        <Text size="sm">ETag Support</Text>
                                        {renderFeatureStatus(config.etag.supported)}
                                    </Group>
                                </Stack>
                            </Paper>

                            <Paper withBorder p="md">
                                <Title order={5} mb="sm">
                                    Filter Configuration
                                </Title>
                                {config.filter.supported && (
                                    <Text size="sm" mb="sm">
                                        <strong>Max Results:</strong> {config.filter.maxResults || 'Unlimited'}
                                    </Text>
                                )}
                                <Divider my="sm" />
                                <Text size="sm" c="dimmed">
                                    Filtering allows clients to query resources using SCIM filter expressions.
                                </Text>
                            </Paper>
                        </SimpleGrid>
                    </Paper>

                    {/* Authentication Schemes */}
                    <Paper withBorder p="lg">
                        <Title order={5} mb="lg">
                            Authentication Schemes
                        </Title>

                        {config.authenticationSchemes && config.authenticationSchemes.length > 0 ? (
                            config.authenticationSchemes.map((scheme, index) => (
                                renderAuthenticationScheme(scheme, index)
                            ))
                        ) : (
                            <Alert color="blue">
                                No authentication schemes configured.
                            </Alert>
                        )}
                    </Paper>

                    {/* Technical Details */}
                    <Paper withBorder p="lg" mt="lg">
                        <Accordion>
                            <Accordion.Item value="technical-details">
                                <Accordion.Control icon={<IconChevronDown size={16} />}>
                                    <Title order={5}>Technical Details</Title>
                                </Accordion.Control>
                                <Accordion.Panel>
                                    <Paper withBorder p="md" bg="var(--mantine-color-default-hover)" ff="monospace" fz="sm">
                                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(config, null, 2)}</pre>
                                    </Paper>
                                </Accordion.Panel>
                            </Accordion.Item>
                        </Accordion>
                    </Paper>
                </>
            )}
        </Box>
    );
};
